import React, { useEffect, useRef } from "react";

const ImageEditor = ({
  imageToEdit,
  setIsEditPopupOpen,
  setMaskPreview,
  maskPreview,
  maskSavedMessage,
  setMaskSavedMessage,
}) => {
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const isDrawingRef = useRef(false);
  const resizeObserverRef = useRef(null);

  // Helper: set brush props after any context reset
  const initBrush = (ctx) => {
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeStyle = "rgba(0, 0, 0, 1)";
    ctx.lineWidth = 25;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  };

  // Helper: map pointer to canvas coordinate space (internal pixels)
  const getCanvasPos = (canvas, clientX, clientY) => {
    const r = canvas.getBoundingClientRect();
    const scaleX = canvas.width / r.width;
    const scaleY = canvas.height / r.height;
    return {
      x: (clientX - r.left) * scaleX,
      y: (clientY - r.top) * scaleY,
    };
  };

  useEffect(() => {
    if (!imageToEdit) return;

    const imgEl = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctxRef.current = ctx;

    const sizeCanvasToImage = () => {
      if (!imgEl.naturalWidth || !imgEl.naturalHeight) return;

      const w = imgEl.naturalWidth;
      const h = imgEl.naturalHeight;

      // Log intrinsic and CSS sizes
      // console.log("Image intrinsic size:", w, "x", h);
      // console.log("Image CSS display size:", imgEl.clientWidth, "x", imgEl.clientHeight);

      const sizeChanged = canvas.width !== w || canvas.height !== h;
      if (sizeChanged) {
        canvas.width = w;
        canvas.height = h;
        initBrush(ctx);
      }

      canvas.style.width = `${imgEl.clientWidth}px`;
      canvas.style.height = `${imgEl.clientHeight}px`;

      // console.log("Mask (canvas) size after sync:", canvas.width, "x", canvas.height);
    };

    const handleImageLoad = () => {
      setTimeout(() => {
        sizeCanvasToImage();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }, 10);
    };

    imgEl.addEventListener("load", handleImageLoad);
    if (imgEl.complete && imgEl.naturalWidth > 0) {
      handleImageLoad();
    }

    const ro = new ResizeObserver(() => {
      canvas.style.width = `${imgEl.clientWidth}px`;
      canvas.style.height = `${imgEl.clientHeight}px`;
    });
    ro.observe(imgEl);
    resizeObserverRef.current = ro;

    // Drawing handlers
    const startDrawing = (clientX, clientY) => {
      isDrawingRef.current = true;
      const p = getCanvasPos(canvas, clientX, clientY);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const draw = (clientX, clientY) => {
      if (!isDrawingRef.current) return;
      const p = getCanvasPos(canvas, clientX, clientY);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    };
    const stopDrawing = () => {
      if (!isDrawingRef.current) return;
      isDrawingRef.current = false;
      ctx.closePath();
    };

    // Mouse handlers
    const onMouseDown = (e) => startDrawing(e.clientX, e.clientY);
    const onMouseMove = (e) => draw(e.clientX, e.clientY);
    const onMouseUp = stopDrawing;
    const onMouseLeave = stopDrawing;

    // Touch handlers
    const onTouchStart = (e) => {
      const t = e.touches[0];
      if (!t) return;
      startDrawing(t.clientX, t.clientY);
      e.preventDefault();
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t) return;
      draw(t.clientX, t.clientY);
      e.preventDefault();
    };
    const onTouchEnd = stopDrawing;

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("mouseleave", onMouseLeave);

    canvas.addEventListener("touchstart", onTouchStart, { passive: false });
    canvas.addEventListener("touchmove", onTouchMove, { passive: false });
    canvas.addEventListener("touchend", onTouchEnd);

    initBrush(ctx);

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("mouseleave", onMouseLeave);

      canvas.removeEventListener("touchstart", onTouchStart);
      canvas.removeEventListener("touchmove", onTouchMove);
      canvas.removeEventListener("touchend", onTouchEnd);

      imgEl.removeEventListener("load", handleImageLoad);
      resizeObserverRef.current?.disconnect();
    };
  }, [imageToEdit]);

  const saveMask = () => {
    const canvas = canvasRef.current;
    const imgEl = imgRef.current;

    // console.log("Saving mask...");
    // console.log("Image intrinsic size:", imgEl.naturalWidth, "x", imgEl.naturalHeight);
    // console.log("Image CSS display size:", imgEl.clientWidth, "x", imgEl.clientHeight);
    // console.log("Mask (canvas) size:", canvas.width, "x", canvas.height);

    const w = canvas.width;
    const h = canvas.height;

    const tmp = document.createElement("canvas");
    tmp.width = w;
    tmp.height = h;
    const tmpCtx = tmp.getContext("2d");

    tmpCtx.fillStyle = "rgba(255, 255, 255, 1)";
    tmpCtx.fillRect(0, 0, w, h);
    tmpCtx.drawImage(canvas, 0, 0, w, h);

    const imgData = tmpCtx.getImageData(0, 0, w, h);
    const data = imgData.data;
    for (let i = 0; i < data.length; i += 4) {
      const g = data[i];
      data[i] = 0;
      data[i + 1] = 0;
      data[i + 2] = 0;
      data[i + 3] = g;
    }
    tmpCtx.putImageData(imgData, 0, 0);

    const maskDataUrl = tmp.toDataURL("image/png");

    setMaskPreview(maskDataUrl);
    setMaskSavedMessage(true);
    setTimeout(() => setMaskSavedMessage(false), 3000);

    return maskDataUrl;
  };

  const handleMaskUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        const originalImage = imgRef.current;

        const targetWidth = originalImage.naturalWidth;
        const targetHeight = originalImage.naturalHeight;

        // console.log("Uploaded mask intrinsic size:", img.naturalWidth, "x", img.naturalHeight);
        // console.log("Target image intrinsic size:", targetWidth, "x", targetHeight);
        // console.log("Target image CSS display size:", originalImage.clientWidth, "x", originalImage.clientHeight);

        canvas.width = targetWidth;
        canvas.height = targetHeight;
        canvas.style.width = `${originalImage.clientWidth}px`;
        canvas.style.height = `${originalImage.clientHeight}px`;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.globalCompositeOperation = "source-over";
        ctx.drawImage(
          img,
          0,
          0,
          img.naturalWidth,
          img.naturalHeight,
          0,
          0,
          targetWidth,
          targetHeight
        );

        // console.log("Mask (canvas) size after upload:", canvas.width, "x", canvas.height);

        const previewCanvas = document.createElement("canvas");
        previewCanvas.width = targetWidth;
        previewCanvas.height = targetHeight;
        const previewCtx = previewCanvas.getContext("2d");
        previewCtx.drawImage(canvas, 0, 0);

        const newPreview = previewCanvas.toDataURL("image/png");
        setMaskPreview(newPreview);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = null;
  };

  const clearMask = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setMaskPreview(null);
  };

  return (
    <div className="files-upload-popup" onClick={(e) => e.stopPropagation()}>
      <div className="files-popup-content">
        <h2>Edit Image</h2>

        <div className="canvas-container">
          <img
            ref={imgRef}
            src={imageToEdit?.url}
            alt="Editable Content"
            id="editable-image"
            draggable={false}
          />
          <canvas ref={canvasRef} id="mask-canvas" />
        </div>

        {maskPreview && (
          <div className="preview-grid">
            <div className="preview-item">
              <h3>Original Image</h3>
              <img src={imageToEdit?.url} alt="Original" className="preview-img" />
            </div>
            <div className="preview-item">
              <h3>Mask Preview</h3>
              <img src={maskPreview} alt="Mask" className="preview-img" />
            </div>
          </div>
        )}

        {maskSavedMessage && (
          <div className="confirmation-message">
            <p>Mask saved successfully!</p>
          </div>
        )}

        <div className="modal-actions">
          <input
            type="file"
            accept="image/png"
            id="upload-mask-input"
            style={{ display: "none" }}
            onChange={handleMaskUpload}
          />
          <button onClick={() => document.getElementById("upload-mask-input").click()}>
            Upload Mask
          </button>
          <button onClick={saveMask}>Save</button>
          <button onClick={clearMask}>Clear</button>
          <button onClick={() => setIsEditPopupOpen(false)}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ImageEditor;
